/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { addToCartEvent, selectPromotion } from "@WhiteCap/Common/Utillities/CustomTracking";
import { SpecialCategoryContext } from "@WhiteCap/Overrides/Widgets/ProductList/ProductListCardList";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;
const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const productDetails = props.productContext;
    const categoriesId = state.pages.productList.filteredApiParameter?.categoryId;

    let category = null;

    if (categoriesId) {
        category = state.data.categories.byId[categoriesId];
    }

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        productDetails,
        cartTotal: getCurrentCartState(state).value?.orderGrandTotal ?? 0,
        session: getSession(state),
        category,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    productDetails,
    cartTotal,
    session,
    category,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const context = useContext(SpecialCategoryContext);
    // EC-1636: Hide add to cart button if the real time pricing is less than $0.01.
    if (
        (!hasEnoughInventory && !failedToLoadInventory) ||
        !canAddToCart ||
        (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price < 0.01)
    ) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price < 0.01) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });

            const price = pricing?.unitNetPrice ?? 0;
            const quantity = productDetails.productInfo?.qtyOrdered ?? qtyOrdered ?? 1;
            const value = price * quantity;
            if (context?.specialCategory && category?.shortDescription) {
                const categoryNames = context.specialCategory.split(",");
                const categoryName = category?.shortDescription;
                if (
                    categoryName &&
                    categoryNames.map(item => item.trim().toLowerCase()).includes(categoryName.toLowerCase())
                ) {
                    if (productDetails.product.badges && productDetails.product.badges.length > 0) {
                        selectPromotion({
                            event: "select_promotion",
                            promotion_id: productDetails.product.badges[0].id,
                            promotion_name: productDetails.product.badges[0].name,
                            items: [
                                {
                                    item_id: productDetails.product.productNumber,
                                    item_name: productDetails.product.productTitle,
                                    price,
                                    quantity,
                                    item_brand: productDetails.product.brand?.name || "",
                                },
                            ],
                        });
                    } else {
                        selectPromotion({
                            event: "select_promotion",
                            promotion_id: category.id,
                            promotion_name: category.name,
                            items: [
                                {
                                    item_id: productDetails.product.productNumber,
                                    item_name: productDetails.product.productTitle,
                                    price,
                                    quantity,
                                    item_brand: productDetails.product.brand?.name || "",
                                },
                            ],
                        });
                    }
                }
            } else if (productDetails.product.badges && productDetails.product.badges.length > 0) {
                selectPromotion({
                    event: "select_promotion",
                    promotion_id: productDetails.product.badges[0].id,
                    promotion_name: productDetails.product.badges[0].name,
                    items: [
                        {
                            item_id: productDetails.product.productNumber,
                            item_name: productDetails.product.productTitle,
                            price,
                            quantity,
                            item_brand: productDetails.product.brand?.name || "",
                        },
                    ],
                });
            }

            addToCartEvent({
                event: "add_to_cart",
                ecommerce: {
                    currency: session?.currency?.currencyCode,
                    value,
                    items: [
                        {
                            item_name: productDetails.product?.productTitle,
                            item_id: productDetails.product?.productNumber,
                            quantity,
                            price,
                            item_brand: productDetails.product?.brand?.name ? productDetails.product?.brand?.name : "",
                        },
                    ],
                },
            });
        }
    };

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {labelOverride ?? translate("Add to Cart")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
